import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  QrCodeScanner as ScanIcon,
  AutoFixHigh as GenerateIcon,
  PictureAsPdf as PdfIcon,
  Code as ZplIcon,
  ContentCopy as CopyCodeIcon,
  AddCircleOutline as NewCodeIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { barcodesApi, FullBarcodeResponse } from "../../services/barcodesApi";
import { BarcodeScanner } from "../BarcodeScanner";

interface BarcodeManagerProps {
  productId: number;
  productCode?: string;
}

const BarcodeManager: React.FC<BarcodeManagerProps> = ({ productId, productCode }) => {
  const { t } = useTranslation();
  const [barcodes, setBarcodes] = useState<FullBarcodeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState<null | HTMLElement>(null);
  const [printBarcodeId, setPrintBarcodeId] = useState<number | null>(null);
  const [generateMenuAnchor, setGenerateMenuAnchor] = useState<null | HTMLElement>(null);

  const fetchBarcodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await barcodesApi.getProductBarcodes(productId);
      setBarcodes(data);
    } catch {
      setBarcodes([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchBarcodes();
  }, [fetchBarcodes]);

  const handleSetPrimary = async (barcodeId: number) => {
    setActionLoading(barcodeId);
    try {
      await barcodesApi.updateBarcode(barcodeId, { is_primary: true });
      await fetchBarcodes();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (barcodeId: number) => {
    setActionLoading(barcodeId);
    try {
      await barcodesApi.deleteBarcode(barcodeId);
      setBarcodes((prev) => prev.filter((b) => b.id !== barcodeId));
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateClick = (event: React.MouseEvent<HTMLElement>) => {
    if (productCode) {
      setGenerateMenuAnchor(event.currentTarget);
    } else {
      handleGenerate(false);
    }
  };

  const handleGenerateClose = () => {
    setGenerateMenuAnchor(null);
  };

  const handleGenerate = async (useProductCode: boolean) => {
    handleGenerateClose();
    setActionLoading(-1);
    try {
      await barcodesApi.generateBarcode(productId, useProductCode || undefined);
      await fetchBarcodes();
    } finally {
      setActionLoading(null);
    }
  };

  const handleScan = async (code: string) => {
    setScannerOpen(false);
    setActionLoading(-2);
    try {
      await barcodesApi.addBarcode(productId, {
        code,
        is_primary: barcodes.length === 0,
      });
      await fetchBarcodes();
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrintClick = (event: React.MouseEvent<HTMLElement>, barcodeId: number) => {
    setPrintMenuAnchor(event.currentTarget);
    setPrintBarcodeId(barcodeId);
  };

  const handlePrintClose = () => {
    setPrintMenuAnchor(null);
    setPrintBarcodeId(null);
  };

  const handlePrint = async (format: "pdf" | "zpl") => {
    if (printBarcodeId) {
      handlePrintClose();
      setActionLoading(printBarcodeId);
      try {
        await barcodesApi.downloadLabel(printBarcodeId, format);
      } catch {
        // silently fail — user will see no file opened
      } finally {
        setActionLoading(null);
      }
    } else {
      handlePrintClose();
    }
  };

  const sourceColor = (source: string) => {
    switch (source) {
      case "manufacturer": return "info";
      case "internal": return "primary";
      case "client": return "warning";
      case "supplier": return "secondary";
      default: return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" color="textSecondary">
          {t("barcodes.title", "Kody kreskowe")}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<ScanIcon />}
            onClick={() => setScannerOpen(true)}
            variant="outlined"
            sx={{ borderColor: "rgba(255,255,255,0.2)" }}
          >
            {t("barcodes.scan", "Skanuj")}
          </Button>
          <Button
            size="small"
            startIcon={<GenerateIcon />}
            onClick={handleGenerateClick}
            variant="outlined"
            disabled={actionLoading === -1}
            sx={{ borderColor: "rgba(255,255,255,0.2)" }}
          >
            {actionLoading === -1 ? (
              <CircularProgress size={16} />
            ) : (
              t("barcodes.generate", "Generuj")
            )}
          </Button>
        </Stack>
      </Stack>

      {barcodes.length === 0 ? (
        <Box
          sx={{
            py: 3,
            textAlign: "center",
            border: "1px dashed rgba(255,255,255,0.15)",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("barcodes.noBarcodes", "Brak kodów kreskowych. Dodaj skanując lub generując nowy.")}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {barcodes.map((bc) => (
            <Box
              key={bc.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: bc.is_primary
                  ? "primary.main"
                  : "rgba(255,255,255,0.1)",
                bgcolor: bc.is_primary
                  ? "rgba(0, 169, 140, 0.08)"
                  : "rgba(255,255,255,0.02)",
                transition: "all 0.2s",
              }}
            >
              <Tooltip
                title={
                  bc.is_primary
                    ? t("barcodes.isPrimary", "Kod główny")
                    : t("barcodes.setAsPrimary", "Ustaw jako główny")
                }
              >
                <IconButton
                  size="small"
                  onClick={() => !bc.is_primary && handleSetPrimary(bc.id)}
                  disabled={actionLoading === bc.id}
                  sx={{ color: bc.is_primary ? "primary.main" : "text.secondary" }}
                >
                  {bc.is_primary ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>

              <Typography
                variant="body1"
                fontFamily="monospace"
                fontWeight={bc.is_primary ? 600 : 400}
                sx={{ flex: 1 }}
              >
                {bc.code}
              </Typography>

              <Chip
                label={bc.barcode_type_display}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "rgba(255,255,255,0.2)",
                  fontSize: "0.7rem",
                }}
              />

              <Chip
                label={bc.source_display}
                size="small"
                color={sourceColor(bc.source) as any}
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />

              <Tooltip title={t("barcodes.print", "Drukuj etykietę")}>
                <IconButton
                  size="small"
                  onClick={(e) => handlePrintClick(e, bc.id)}
                  sx={{ color: "text.secondary" }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("common.delete", "Usuń")}>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(bc.id)}
                  disabled={actionLoading === bc.id}
                  sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                >
                  {actionLoading === bc.id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintClose}
      >
        <MenuItem onClick={() => handlePrint("pdf")}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePrint("zpl")}>
          <ListItemIcon><ZplIcon fontSize="small" /></ListItemIcon>
          <ListItemText>ZPL (Zebra)</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={generateMenuAnchor}
        open={Boolean(generateMenuAnchor)}
        onClose={handleGenerateClose}
      >
        {productCode && (
          <MenuItem onClick={() => handleGenerate(true)}>
            <ListItemIcon><CopyCodeIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={t("barcodes.useProductCode", "Użyj kodu produktu")}
              secondary={productCode}
              secondaryTypographyProps={{ fontFamily: "monospace", fontSize: "0.75rem" }}
            />
          </MenuItem>
        )}
        <MenuItem onClick={() => handleGenerate(false)}>
          <ListItemIcon><NewCodeIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t("barcodes.generateNew", "Generuj nowy kod")} />
        </MenuItem>
      </Menu>

      <BarcodeScanner
        open={scannerOpen}
        onScan={handleScan}
        onClose={() => setScannerOpen(false)}
        title={t("barcodes.addByScanning", "Dodaj kod skanując")}
      />
    </Box>
  );
};

export default BarcodeManager;
